import React, { CSSProperties } from "react";
import style from "./tab-bar-better.module.css";
import cn from "classnames";
import { useTabBar } from "./use-tab-bar";
import { useIsCIMReorderEnabled } from "utils/url-utils";

/*
  ======================
  === Simple Tab Bar ===
  ======================

  Example usage:
  ┌────────────────────────────────────────────────────────────────────────────┐
  │                                                                            │
  │ //const enums are turned to numbers in typescript compilation              │
  │ const enum Tab {                                                           │
  │   NewSession = 0,                                                          │
  │   Results = 1,                                                             │
  │ }                                                                          │
  │                                                                            │
  │ const [activeTab, setActiveTab] = useState<Tab>(Tab.NewSession);           │
  │ <TabBar onTabChange={setActiveTab}>                                        │
  │   <span>New session</span>                                                 │
  │   <span>Results</span>                                                     │
  │ </TabBar>                                                                  │
  │ activeTab==Tab.NewSession ? .....                                          │
  │                                                                            │
  └────────────────────────────────────────────────────────────────────────────┘
 */

type TabBarProps = React.PropsWithChildren<{
  initialSelected?: number;
  onTabChange?: (i: number) => void;
  theme?: "light" | "dark";
  customStyle?: CSSProperties;
}>;

type TabBarStyling = Partial<CSSProperties>;

type GliderStyling = Partial<CSSProperties>;

const containerClass = cn(
  style["tab-container"],
  style.fontPoppins,
  style.font12,
  style.widthFull,
  style.colorTextDark
);
const gliderClass = style["tab-glider"];

export const TabBar = ({ theme = "light", children, initialSelected = 0, onTabChange, customStyle }: TabBarProps) => {
  const tabProps = useTabBar(initialSelected, onTabChange);
  const tabItems = React.Children.toArray(children);
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const renderTabContent = (item: typeof tabItems[number]) => {
    // If it's a React component function
    if (typeof item === "function") {
      const Component = item;
      return React.createElement(Component as any);
    }

    // If it's a React element (from children)
    if (React.isValidElement(item)) {
      return item;
    }

    return null;
  };

  if (tabItems.length === 0) {
    return null;
  }

  return (
    <div className={theme == "light" ? style.lightTheme : style.darkTheme}>
      <div className={cn(containerClass, isCimReorderEnabled && style.cimContainer)} style={customStyle}>
        {/* Glider */}
        <div className={gliderClass} style={tabProps.gliderStyle} />

        {/* Tabs */}
        {tabItems.map((item, index) => (
          <button
            key={index}
            {...tabProps.getTabProps(index)}
            className={cn(style["tab-button"], style.centerContent, tabProps.activeTab === index && style.opacity1)}
          >
            {renderTabContent(item)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const CimWidgetsMenuTabBar = ({
  children,
  onTabChange,
  initialSelected = 0,
  tabBarStyle,
  gliderStyle,
}: React.PropsWithChildren<{
  onTabChange?: (i: number) => void;
  initialSelected?: number;
  tabBarStyle?: TabBarStyling;
  gliderStyle?: GliderStyling;
}>) => {
  const tabProps = useTabBar(initialSelected, onTabChange);
  const tabItems = React.Children.toArray(children);

  const renderTabContent = (item: typeof tabItems[number]) => {
    // If it's a React component function
    if (typeof item === "function") {
      const Component = item;
      return React.createElement(Component as any);
    }

    // If it's a React element (from children)
    if (React.isValidElement(item)) {
      return item;
    }

    return null;
  };

  if (tabItems.length === 0) {
    return null;
  }

  return (
    <div className={containerClass} style={tabBarStyle}>
      {/* Glider */}
      <div className={gliderClass} style={{ ...gliderStyle, ...tabProps.gliderStyle }} />

      {/* Tabs */}
      {tabItems.map((item, index) => (
        <button
          key={index}
          {...tabProps.getTabProps(index)}
          className={cn(
            style["tab-button"],
            style.colorTextDark,
            style.centerContent,
            tabProps.activeTab === index && style.opacity1
          )}
        >
          {renderTabContent(item)}
        </button>
      ))}
    </div>
  );
};
